import { Card, CardContent } from "@/lib/shadcn/ui/card";
import * as React from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Convert } from "@/models/responses";
import kerosLogo from "@/staticfiles/KH-logo-01.png";
import { apiUrl } from "@/layouts/apiUrl";

type LoginPageProps = {
    logoutAndClearSessions?: boolean;
};

const LoginPage: React.FC<LoginPageProps> = ({ logoutAndClearSessions }) => {
    const { user, login, logout } = useAuth();
    useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const isAuthenticated = queryParams.get("authenticated") === "true";

    if (logoutAndClearSessions) {
        logout();
        // eslint-disable-next-line react-hooks/immutability
        window.location.href = `${apiUrl}/api/logout`;
    }

    if (user) {
        return <Navigate to="/" />;
    }

    if (isAuthenticated) {
        fetch(`${apiUrl}/api/user?_=${new Date().getTime()}`, {
            credentials: "include",
        })
            .then((response) => {
                if (response) {
                    return response.text();
                }
                throw new Error("Unauthorized");
            })
            .then((response) => {
                const user = Convert.toGetUserResponse(response).user;
                login(user);
            })
            .catch((error) => console.error("Error:", error));
    }

    const handleGoogleLogin = () => {
        window.location.href = `${apiUrl}/api/login`;
    };

    return (
        <main className="min-h-screen bg-gray-50">
            <div
                className="min-h-[41vh] bg-[url('https://appsrv1-147a1.kxcdn.com/volt-dashboard-pro-v131/img/illustrations/signin.svg')]
         bg-center bg-cover flex items-center justify-center py-20"
            >
                <div className="w-full max-w-[700px] ">
                    <Card className="bg-white shadow-lg border border-gray-100">
                        <CardContent className="p-8 sm:p-12">
                            <div className="space-y-8">
                                {/* Logo */}
                                <div className="text-center">
                                    <a href="https://keroshospitality.com" target="_blank" rel="noreferrer" className="inline-block">
                                        <img src={kerosLogo} alt="Keros Hospitality Logo" className="mx-auto w-3/4" />
                                    </a>
                                </div>

                                {/* Google Sign In Button */}
                                <button
                                    onClick={handleGoogleLogin}
                                    className="w-full flex items-center justify-between px-4 py-2.5 bg-[#131314] text-white rounded-full
                  border border-[#747775] hover:shadow-md transition-shadow duration-200
                  disabled:bg-opacity-40 disabled:border-opacity-12 disabled:cursor-not-allowed"
                                >
                                    <div className="flex items-center">
                                        <div className="w-5 h-5 mr-3">
                                            <svg viewBox="0 0 48 48">
                                                <path
                                                    fill="#EA4335"
                                                    d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                                                />
                                                <path
                                                    fill="#4285F4"
                                                    d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                                                />
                                                <path
                                                    fill="#FBBC05"
                                                    d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                                                />
                                                <path
                                                    fill="#34A853"
                                                    d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                                                />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-medium">Sign in with Google</span>
                                    </div>
                                </button>

                                {/* Privacy Policy */}
                                <div className="text-center">
                                    <a href="/privacy-policy" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                                        Privacy Policy
                                    </a>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </main>
    );
};

export default LoginPage;
